import { useEffect, useRef } from 'react'
import { Platform } from 'react-native'

import { useNavigation, useRoute } from '@react-navigation/native'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { publishEvent } from '@zet/analytics'
import { zetApi } from '@zet/network'
import { Method } from 'axios'

import useFormStore from '../modules/formBuilder/store'
import { redirectionStates } from '../utils'
import checkIsJson from '../utils/checkIsJSON'
import { partnerIdMap } from '../utils/partnerIdMap'
import { state } from '../utils/types'
import {
  useGetApplicationDetails,
  useIsApplicationV2,
} from './applicationDetails/hook'
import API from './endpoints'

interface Props {
  endpoint: string
  method: Method
  body?: any
  headers?: any
  params?: any
  state: state
}

export const useApiAction = ({
  skipInitFetch,
  refresh,
  isProgressPage,
}: {
  skipInitFetch?: boolean
  isProgressPage?: boolean
  refresh?: {
    enabled?: boolean
    refreshIntervalInSec?: number
  }
} = {}) => {
  const queryClient = useQueryClient()
  const navigation = useNavigation()
  const { params: postBodyParameters, setParams: setPostBodyParams } =
    useFormStore((state) => state)
  const interval = useRef<ReturnType<typeof setInterval> | null>(null)
  const isLoadingRef = useRef(false)

  const { params, name } = useRoute()
  const { state, hash, forceFetch } = params ?? {}

  const isV2 = useIsApplicationV2(hash)

  const isLOS = name === 'BrandsLOS' ? true : false

  const {
    data: { partnerId },
  } = useGetApplicationDetails(hash)

  const { data: cacheData, dataUpdatedAt } = useQuery(
    ['cc/brands', partnerId, state],
    {
      enabled: false,
    }
  )

  const { mutate, isLoading } = useMutation({
    mutationFn: (props: Props) => {
      const { endpoint, method, body = {}, headers = {}, params, state } = props

      const url =
        typeof endpoint === 'function' ? endpoint(isLOS, isV2) : endpoint

      return zetApi.request({
        url,
        method,
        data: body,
        params,
        headers,
      })
    },
    onSuccess: (data, variables) => {
      if (variables?.body?.method === 'GET') {
        const resp = data?.data?.data
        publishEvent('screen_view', {
          screen_name: 'CC_FULL_INTEGRATION',
          page_name: resp?.state,
          lead_ID: resp?.metadata?.leadId,
          customer_ID: resp?.metadata?.customerId,
          partner_ID: resp?.metadata?.partnerId,
          brand_name: partnerIdMap?.[String(resp?.metadata?.partnerId)] ?? '',
          brand_category: '15',
        })
      }

      if (data?.data) {
        if (
          redirectionStates?.includes(data?.data?.data?.state) &&
          data?.data?.data?.metadata?.redirect_url
        ) {
          window?.open?.(data?.data?.data?.metadata?.redirect_url, '_self')
        } else {
          queryClient.setQueryData(
            [
              'cc/brands',
              partnerId,
              isProgressPage ? variables?.state : data?.data?.data?.state,
            ],
            {
              ...data?.data,
            },
            {
              updatedAt: Date.now(),
            }
          )
          const kycRedirectionLink = data?.data?.data?.metadata?.kycRedirectUrl
          const redirect_url = data?.data?.data?.metadata?.redirect_url
          if (kycRedirectionLink || redirect_url) {
            if (Platform.OS === 'web') {
              if (!checkIsJson(redirect_url)) {
                window.location.href = redirect_url
              } else if (kycRedirectionLink) {
                window.location.href = kycRedirectionLink
              }
            }
          }
          if (data?.data?.data?.state !== variables.state && !isProgressPage) {
            navigation.replace(isLOS ? 'BrandsLOS' : 'BrandsCC', {
              state: data?.data?.data?.state,
              hash,
            })
          }
        }
      }
    },
    onError: (error) => {
      const redirectState = 'HDFC_CC_SOMETHING_WENT_WRONG'
      if (error?.response?.status === 500) {
        switch (partnerId) {
          case 299:
            if (state !== redirectState)
              navigation.replace(isLOS ? 'BrandsLOS' : 'BrandsCC', {
                hash,
                state: redirectState,
              })
            break
        }
      }
    },
  })

  useEffect(() => {
    // const cacheData = queryClient.getQueryData(['cc/brands', state])

    if (
      !skipInitFetch &&
      hash &&
      (forceFetch || isProgressPage || Date.now() - dataUpdatedAt > 500)
    ) {
      mutate({
        method: 'POST',
        endpoint: API.stateManage.endpoint(isLOS, isV2),
        state,
        headers: { hash: encodeURIComponent(hash) },
        body: {
          method: 'GET',
          ...(isProgressPage && { isProgressPage }),
          ...(!isProgressPage && { state }),
        },
      })
    }
  }, [hash])

  useEffect(() => {
    isLoadingRef.current = isLoading
  }, [isLoading])

  useEffect(() => {
    if (!refresh?.enabled && interval.current) {
      clearInterval(interval.current)
    }
    if (hash && refresh?.enabled) {
      interval.current = setInterval(() => {
        mutate({
          method: 'POST',
          endpoint: API.stateManage.endpoint(isLOS, isV2),
          state,
          headers: { hash: encodeURIComponent(hash) },
          body: {
            method: 'GET',
            ...(isProgressPage && { isProgressPage }),
            ...(!isProgressPage && { state }),
          },
        })
      }, refresh?.refreshIntervalInSec * 1000)
    }

    return () => {
      interval.current && clearInterval(interval.current)
    }
  }, [hash, refresh?.enabled])

  const clearCheckStatusInterval = () => {
    interval.current && clearInterval(interval.current)
  }

  const stateManagePost = ({
    payload,
    event,
    forceRefetch = false,
    options = {},
    extras = {},
  } = {}) => {
    if (cacheData?.data?.metadata?.nextState) {
      navigation.replace(isLOS ? 'BrandsLOS' : 'BrandsCC', {
        hash,
        state: cacheData?.data?.metadata?.nextState,
      })
    } else {
      const headers = { hash: encodeURIComponent(hash) }
      const endpoint = API.stateManage.endpoint(isLOS, isV2)
      const endpointConfig = API.stateManage
      let body = {
        state: state,
        method: 'POST',
        ...postBodyParameters,
        ...(event && { event }),
        ...(payload && { payload }),
      }

      setPostBodyParams({})

      if (state === 'HDFC_CC_OTP_GENERATE') {
        body = { ...body, event: 'SUBMIT_OTP' }
      }

      body = { ...body, ...extras }

      mutate(
        {
          headers,
          ...endpointConfig,
          endpoint,
          body,
          state,
        },
        options
      )
    }
  }

  return {
    mutate,
    isLoading,
    data: cacheData?.data,
    error: cacheData?.error,
    hash,
    state,
    stateManagePost,
    navigation,
    clearCheckStatusInterval,
  }
}

/**
 * check whether data exists for the state - no -> make get api call | yes -> render
 *
 *
 * check whether next state is present - yes -> navigate | no -> next line
 * make post api call - different state -> navigate, same state -> update data
 */
